import React, { memo, forwardRef, useImperativeHandle, useCallback, useRef } from "react";
import useLocalFocusModeToggle from '@util/hook/useLocalFocusModeToggle';
import FocusModeToggleButton from '@icoach/components/FocusModeToggleButton';
import { Box } from "@curves/ui-base";
import clsx from 'clsx';
import useMessageDialog from '@util/hook/useMessageDialog';
import EmployeeIdentityVerificationDialog from '@icoach/components/EmployeeIdentityVerificationDialog';
import useSwitch from '@util/hook/useSwitch';
import { useSelector } from "react-redux";
import { getVersionData } from "@redux/version/selector";
import useConditionalNavigationGuard from '@util/hook/useConditionalNavigationGuard';

/**
 * FocusModeToggle - 管理專注模式（Focus Mode）的切換功能
 * 1. 允許使用者進入或離開專注模式
 * 2. 離開專注模式時，需要通過身份驗證
 * 3. 使用導航守衛來防止未確認的導航行為
 * 4. 提供 `ref` 給父組件，允許外部觸發焦點模式的退出
 */
const FocusModeToggle = memo(
    forwardRef(({ children, showToggleFocusModeButton = true, defaultFocusMode = false }, ref) => {
        // 控制身份驗證dialog的開關
        const { isOn: isOpenDialog, handleTurnOn: handleOpenDialog, handleTurnOff: handleCloseDialog } = useSwitch();

        const {env} = useSelector(getVersionData);

        const _defaultFocusMode = env === "Dev" ? false : defaultFocusMode; // dev 不要專注

        // 取得專注模式的狀態與控制函數
        const { isFocusMode, onEnableFocusMode, onDisableFocusMode } = useLocalFocusModeToggle(_defaultFocusMode);

        const setMessageDialog = useMessageDialog();

        // 返回攔截：依專注模式狀態顯示對應 dialog
        const { setGuardEnabled } = useConditionalNavigationGuard({
            isConditionMet: isFocusMode,
            defaultEnabled: showToggleFocusModeButton,
            onNavigationBlocked: ({ isConditionMet: focusModeActive, confirmNavigation, cancelNavigation }) => {
                if (focusModeActive) {
                    setMessageDialog({
                        open: true,
                        title: '目前無法離開頁面',
                        msg: '目前處於專注模式，請先退出專注模式後再離開。',
                        buttonText: '知道了',
                        onAsyncConfirm: (e, onClose) => {
                            cancelNavigation();
                            onClose();
                        },
                        onClose: cancelNavigation,
                    });
                } else {
                    setMessageDialog({
                        open: true,
                        title: '確定要離開？',
                        msg: '此動作將會遺失資料，確定要離開？',
                        onAsyncConfirm: async (e, onClose) => {
                            await confirmNavigation();
                            onClose();
                        },
                        onClose: cancelNavigation,
                    });
                }
            },
        });

        // 用於存儲外部傳入的刷新函數，當焦點模式關閉時執行
        const refreshFnRef = useRef(null);

        /**
         * 處理身份驗證結果
         * @param {boolean} success - 是否驗證成功（預設為 true）
         */
        const handleVerificationOutcome = useCallback(
            (success = true) => {
                if (success) {
                    if (typeof refreshFnRef.current === 'function') {
                        setGuardEnabled(false); // 取消導航守衛
                        refreshFnRef.current();
                    }
                    onDisableFocusMode(); // 關閉焦點模式
                    handleCloseDialog(); // 關閉身份驗證彈窗
                }
            },
            // eslint-disable-next-line
            [],
        );

        /**
         * 切換專注模式
         */
        const toggleFocusMode = useCallback(
            () => {
                if (!isFocusMode) {
                    onEnableFocusMode();
                } else {
                    handleOpenDialog();
                }
            },
            // eslint-disable-next-line
            [isFocusMode],
        );

        /**
         * 提供 `ref`，允許父組件透過 `checkAndExitFocusMode` 來退出專注模式
         */
        useImperativeHandle(
            ref,
            () => ({
                /**
                 * 檢查並退出專注模式
                 * @param {Function} fn - 外部傳入的刷新函數
                 */
                checkAndExitFocusMode: (fn) => {
                    if (isFocusMode) {
                        refreshFnRef.current = fn; // 暫存外部傳入的函數
                        handleOpenDialog(); // 開啟身份驗證彈窗
                    } else {
                        refreshFnRef.current = null; // 清空暫存的函數
                        setGuardEnabled(false); // 取消導航守衛
                        if (typeof fn === 'function') {
                            fn(); // 執行傳入的函數
                        }
                    }
                },
            }),
            // eslint-disable-next-line
            [isFocusMode],
        );

        return (
            <Box className={clsx('focus-mode', { active: isFocusMode })}>
                {/* 專注模式切換按鈕（若允許顯示） */}
                {showToggleFocusModeButton && <FocusModeToggleButton className={'focus-mode__button'} isFocusMode={isFocusMode} onClick={toggleFocusMode} />}
                {children}

                {/* 身份驗證彈窗（當退出專注模式時顯示） */}
                <EmployeeIdentityVerificationDialog
                    open={isOpenDialog}
                    onClose={handleCloseDialog}
                    onIdentityVerificationResult={handleVerificationOutcome}
                />
            </Box>
        );
    }),
);

export default FocusModeToggle;
